import { Controller } from "@hotwired/stimulus"
import { TempusDominus } from '@eonasdan/tempus-dominus'

export default class extends Controller {
  static values = { 
    format: { type: String, default: 'dd.MM.yyyy' },
    linkedMin: String,  // Selector for field that sets minDate
    linkedMax: String,  // Selector for field that sets maxDate
    disabledDates: { type: Array, default: [] }  // Array of disabled dates in YYYY-MM-DD format
  }
  
  connect() {
    // Parse disabled dates
    const disabledDates = this.disabledDatesValue.map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    })
    
    this.picker = new TempusDominus(this.element, {
      display: {
        components: {
          clock: false,
          hours: false,
          minutes: false,
          seconds: false
        },
        icons: {
          type: 'icons',
          time: 'bi bi-clock',
          date: 'bi bi-calendar-week',
          up: 'bi bi-arrow-up',
          down: 'bi bi-arrow-down',
          previous: 'bi bi-chevron-left',
          next: 'bi bi-chevron-right',
          today: 'bi bi-calendar-check',
          clear: 'bi bi-trash',
          close: 'bi bi-x'
        }
      },
      localization: {
        format: this.formatValue
      },
      restrictions: {
        disabledDates: disabledDates
      }
    })
    
    // Set initial restrictions based on linked fields
    this.updateRestrictions()
    
    // Subscribe to change events and dispatch input event for parent controllers
    this.picker.subscribe('change.td', (e) => {
      if (e.date) {
        // Format date as YYYY-MM-DD for backend storage
        const year = e.date.year
        const month = String(e.date.month + 1).padStart(2, '0')
        const day = String(e.date.date).padStart(2, '0')
        
        // Store the backend format in a data attribute
        this.element.dataset.backendValue = `${year}-${month}-${day}`
      } else {
        this.element.dataset.backendValue = ''
      }
      
      // Dispatch input event so inline-tenant controller can catch the change
      this.element.dispatchEvent(new Event('input', { bubbles: true }))
      
      // Dispatch custom event so linked datepickers can update their restrictions
      this.element.dispatchEvent(new CustomEvent('datepicker:change', { bubbles: true }))
    })
    
    // Listen for changes from linked datepickers
    this.boundUpdateRestrictions = this.updateRestrictions.bind(this)
    document.addEventListener('datepicker:change', this.boundUpdateRestrictions)
  }
  
  disconnect() {
    document.removeEventListener('datepicker:change', this.boundUpdateRestrictions)
    if (this.picker) {
      this.picker.dispose()
      this.picker = null
    }
  }
  
  updateRestrictions() {
    // Find linked fields within the same row
    const row = this.element.closest('tr')
    if (!row) return
    
    // If this field has a linkedMin (e.g., lease_end_date linked to lease_start_date)
    if (this.hasLinkedMinValue) {
      const minField = row.querySelector(this.linkedMinValue)
      if (minField && minField.value) {
        const minDate = this.parseDate(minField.value)
        if (minDate) {
          this.picker.updateOptions({ restrictions: { minDate: minDate } })
        }
      }
    }
    
    // If this field has a linkedMax (e.g., lease_start_date linked to lease_end_date)
    if (this.hasLinkedMaxValue) {
      const maxField = row.querySelector(this.linkedMaxValue)
      if (maxField && maxField.value) {
        const maxDate = this.parseDate(maxField.value)
        if (maxDate) {
          this.picker.updateOptions({ restrictions: { maxDate: maxDate } })
        }
      }
    }
  }
  
  parseDate(dateStr) {
    // Parse dd.MM.yyyy format
    const parts = dateStr.split('.')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      return new Date(year, month, day)
    }
    return null
  }
}
